import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Spinner, Table } from 'react-bootstrap';
import { usePagedArray, Pager } from 'rentalui-finance';
import { listMyAgentRequests } from '../api';
import RequestAgentModal from './RequestAgentModal';

// 03_requirements_missing.md story 3 - a rental owner's view of their site-visit agent onboarding
// requests, plus the button to raise a new one. Approved requests become `delivery`-role accounts
// the owner can then assign to visits (AssignAgentModal).
export default function OwnerAgentsPanel() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [submittedMsg, setSubmittedMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRequests(await listMyAgentRequests());
      setError('');
    } catch {
      setError('Could not load your agent requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const paged = usePagedArray(requests);

  if (loading) return <Spinner animation="border" role="status" />;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <p className="text-muted small mb-0">
          Agents show your properties to visitors. You can't create accounts directly — request one
          and the website owner onboards it.
        </p>
        <Button size="sm" onClick={() => setShowModal(true)}>+ Request an agent</Button>
      </div>
      {error && <Alert variant="danger" className="py-2">{error}</Alert>}
      {submittedMsg && <Alert variant="success" dismissible onClose={() => setSubmittedMsg('')}>{submittedMsg}</Alert>}

      {paged.total === 0 ? (
        <Alert variant="light">You haven't requested any agents yet.</Alert>
      ) : (
        <>
        <Table responsive hover>
          <thead>
            <tr><th>Candidate</th><th>Service area</th><th>Status</th><th>Reviewer note</th></tr>
          </thead>
          <tbody>
            {paged.rows.map((r) => (
              <tr key={r._id}>
                <td>{r.name}<div className="small text-muted">{r.email}{r.phone ? ` · ${r.phone}` : ''}</div></td>
                <td>{r.serviceArea || '—'}</td>
                <td>
                  <Badge bg={r.status === 'approved' ? 'success' : r.status === 'rejected' ? 'danger' : 'warning'} text={r.status === 'pending' ? 'dark' : undefined}>
                    {r.status}
                  </Badge>
                </td>
                <td className="small text-muted">{r.reviewerNote || '—'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Pager {...paged} />
        </>
      )}

      <RequestAgentModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSubmitted={() => {
          setShowModal(false);
          setSubmittedMsg('Request submitted — the website owner will review it.');
          load();
        }}
      />
    </div>
  );
}
